import React, { useEffect, useState } from 'react'

import Button from '@instabox/internal-web-ui/components/Button'
import Table from '@instabox/internal-web-ui/components/Table'
import ResetIcon from '@instabox/internal-web-ui/icons/ResetIcon'
import { Rollout } from '@instabox/lockers-update-common/types'
import { orderBy } from 'lodash'

import usePackages from '../../hooks/usePackages'
import Dropdown from '../components/Dropdown'

export interface PackageVersion {
  name: string
  version: string
}

interface RolloutPackageTable {
  onPackagesChanged: (packages: PackageVersion[]) => void
  currentRollout: Rollout | undefined
  loading: boolean
}

const RolloutPackagesTable: React.FC<RolloutPackageTable> = ({
  onPackagesChanged,
  currentRollout,
  loading: rolloutLoading,
}) => {
  const { packages, loading: packagesLoading, reload: reloadPackages } = usePackages()
  const [rolloutPackages, setRolloutPackages] = useState<PackageVersion[]>([])

  useEffect(() => {
    if (rolloutPackages.length === 0) {
      return
    }

    onPackagesChanged(rolloutPackages)
  }, [rolloutPackages, onPackagesChanged])

  useEffect(() => {
    if (currentRollout === undefined) {
      return
    }
    setRolloutPackages(currentRollout.packages.filter((a) => packages.some((b) => a.name === b.name)))
  }, [currentRollout, packages])

  useEffect(() => {
    if (packagesLoading || rolloutLoading) {
      return
    }

    if (rolloutPackages.length !== 0) {
      return
    }

    if (currentRollout !== undefined) {
      return
    }

    setRolloutPackages(
      packages
        .filter((e) => {
          return e.rolloutCandidate
        })
        .map((p) => ({
          name: p.name,
          version: '',
        }))
    )
  }, [packages, currentRollout, rolloutPackages, packagesLoading, rolloutLoading])

  function handlePackageVersionChanged(event: React.ChangeEvent<HTMLSelectElement>, name: string): void {
    event.preventDefault()
    const version = event.target.value

    const rest = rolloutPackages.filter((p) => p.name !== name)

    setRolloutPackages([
      ...rest,
      {
        name,
        version,
      },
    ])
  }

  return (
    <div>
      <div className="pb-4 d-flex justify-content-end">
        <Button
          shape="outlined"
          isLoading={packagesLoading}
          icon={<ResetIcon />}
          iconPosition="right"
          variant="light"
          data-testid="reload-packages-button"
          onClick={() => reloadPackages(true, true)}
        >
          Refresh Packages
        </Button>
      </div>

      <div className="pb-4">
        <Table
          style={{ overflow: 'hidden' }}
          isLoading={packagesLoading || rolloutLoading}
          headers={{
            name: 'Package',
            version: 'Version',
          }}
          rows={packages
            .filter((pkg) => pkg.rolloutCandidate)
            .map((pkg) => {
              const versions = pkg.versions ?? []
              const options: string[] = orderBy(versions, (v) => v.createdAt, 'desc').map((version) => version.version)

              return {
                name: pkg.name,
                version: (
                  <Dropdown
                    data-testid={`versionDropDown-${pkg.name}`}
                    options={options}
                    value={rolloutPackages.find((p) => p.name === pkg.name)?.version || ''}
                    onChange={(e) => handlePackageVersionChanged(e, pkg.name)}
                  />
                ),
              }
            })}
        />
      </div>
    </div>
  )
}

export default RolloutPackagesTable
